import { z } from "zod";
import { CREW, NAME_MAX, SHIP_SLOTS } from "./config.ts";
import type { MoveInput } from "./physics.ts";
import { ROPES, SAILS } from "./damage.ts";
import { SHIP_POINTS } from "./ship.ts";
import { ISLAND_IDS, SUPPLY_BARRELS, type IslandId } from "./world.ts";
import { GATE_LEVERS, TELESCOPES } from "./interactives.ts";
import { LANTERN_INTERACTION_IDS, type LanternInteractionId } from "./lantern.ts";

/** Client → server message names. */
export const C2S = {
  input: "in",
  use: "use",
  hold: "hold",
  dig: "dig",
  drop: "drop",
  sail: "sail",
  abandon: "abandon",
  vote: "vote",
  recover: "recover",
  debug: "debug",
  crew: "crew",
  fire: "fire",
  loadSelf: "loadSelf",
  equip: "equip",
  attack: "attack",
  block: "block",
  shoot: "shoot",
  reload: "reload",
  bot: "bot",
  /** Left click with the held bucket: scoop deck flood water into an empty one. */
  fill: "fill",
  /** Left click with the held bucket: throw a full one over the rail. */
  throw: "throw",
  /** Change this player's character look: a look code (look.ts) or "" for the classic crewmate. */
  look: "look",
} as const;

/** Server → client message names. */
export const S2C = {
  toast: "toast",
  fx: "fx",
  sale: "sale",
  digResult: "digResult",
  meleeResult: "meleeResult",
  attackAck: "attackAck",
  pistolShot: "pistolShot",
  debug: "debugResult",
  board: "board",
  islandRead: "islandRead",
} as const;

export interface InputMsg extends MoveInput {
  seq: number;
}

/** Interaction targets. Chests use `chest:<id>`, leaks `leak:<slot>`, ship cannons `cannon:<index>`, shore cannons `islandCannon:<id>`, barrels `barrel:<id>`, authored Lantern objects `lantern:<id>`, gate levers `lever:<index>` and mounted telescopes `telescope:<index>`. `bucket` takes or returns a bucket at the ship's rack. */
export type QuestKind = "map" | "hunt";
export type UseTarget = "wheel" | "capstan" | "questBoard:map" | "questBoard:hunt" | "merchant" | "bucket" | "supplies" | "bell" | "nestScope" | `chest:${string}` | `leak:${number}` | `cannon:${number}` | `shipLantern:${number}` | `islandCannon:${IslandId}` | `barrel:${number}` | `lantern:${LanternInteractionId}` | `lever:${number}` | `telescope:${number}`;

/** Stations that look through a telescope: island telescopes and the crow's nest scope. */
export const isScopeStation = (station: string): boolean => station.startsWith("telescope:") || station === "nestScope";

/**
 * Held work: repair a hole (`leak:<slot>`), rebuild the smashed or hacked helm (`wheel`), remount a
 * dismounted gun (`cannon:<index>`), splice a cut rope (`rope:<ROPES index>`), patch or re-bend a
 * sail (`sail:<SAILS index>`), or walk a capstan bar round to raise the anchor (`use` "capstan" drops it).
 */
export type HoldTarget = "capstan" | "wheel" | `leak:${number}` | `cannon:${number}` | `rope:${number}` | `sail:${number}`;
/** Selectable tools (`equip`); `bucket` is only taken from the ship's rack. */
export type Tool = "sword" | "shovel" | "spyglass" | "pistol";
export type Equipment = Tool | "bucket";

export interface IslandReadMsg {
  id: LanternInteractionId;
}

export interface AttackMsg {
  requestId: number;
}

export interface AttackAckMsg {
  requestId: number;
  status: "started" | "queued" | "rejected";
  serial: number;
  step: number;
}

export interface MeleeResultMsg {
  attacker: string;
  target: string;
  serial: number;
  step: number;
  result: "hit" | "blocked" | "parried" | "miss";
  x: number;
  y: number;
  z: number;
}
export interface ShootMsg {
  yaw: number;
  pitch: number;
  yawFrame: number;
}

export interface PistolShotMsg {
  shooter: string;
  serial: number;
  target: string;
  x: number;
  y: number;
  z: number;
  hitX: number;
  hitY: number;
  hitZ: number;
  impact: "none" | "solid" | "water" | "player";
}


export type ToastKind = "info" | "good" | "warn" | "bad";

export interface ToastMsg {
  kind: ToastKind;
  text: string;
}

export type FxKind =
  /** A hull hit rock, shore or a pier. */
  | "impact"
  /** Two hulls collided; at the contact point. */
  | "ram"
  | "leak"
  | "repaired"
  | "dig"
  | "treasure"
  | "pickup"
  | "drop"
  | "sold"
  | "sinking"
  | "splash"
  | "bail"
  | "anchorDrop"
  | "anchorSplash"
  | "anchorClick"
  | "anchorStowed"
  | "sail"
  | "milestone"
  | "death"
  | "respawn"
  | "drown"
  | "cannon"
  | "ballHit"
  | "ballSplash"
  | "ballLand"
  | "playerHit"
  | "pistol"
  | "pistolImpact"
  | "restock"
  | "crateHit"
  | "crateBreak"
  | "gate"
  | "bell"
  | "lantern"
  /** A ram at or above SHIP.damageImpactSpeed: timbers crushing at the contact. */
  | "ram"
  /** A rope parting under shot or a blade. */
  | "ropeSnap"
  /** Canvas holed or torn away. */
  | "sailTear"
  /** The helm hacked or shot to pieces. */
  | "wheelSmash"
  /** A gun knocked off its carriage. */
  | "gunWreck";

/** Optional event context; absent fields mean "not applicable". */
export interface FxContext {
  /** 0 … 1 strength (collision speed). */
  power?: number;
  /** Crew slot of the ship the event happened to (rammed or struck hull). */
  ship?: number;
  /** Crew slot that caused it (rammer, cannon owner). */
  by?: number;
}

export interface FxMsg extends FxContext {
  kind: FxKind;
  x: number;
  y: number;
  z: number;
}

export interface SaleMsg {
  earned: number;
  total: number;
  quests: number;
  title: string;
  newTitle: boolean;
  seller: string;
  /** Crew name of the seller. */
  crew: string;
  chest: string;
}

export type DigResult = "nothing" | "noQuest" | "progress" | "found" | "clue" | "cooldown" | "invalid";

export interface DigResultMsg {
  result: DigResult;
  progress: number;
}

/**
 * `damage` (debug only) damages or repairs the caller's ship: args `[part, index?, amount?]` with part
 * `rope` (cut ROPES[index]), `sail` (hole cell `amount` of SAILS[index]), `shred` (tear SAILS[index] away),
 * `wheel` (`index` damage to the helm), `cannon` (dismount gun `index`), `breach` (widen hole `index`
 * by `amount` levels) or `repair` (restore everything).
 */
export type DebugCommand = "teleportShip" | "forceQuest" | "setFlooding" | "openLeak" | "setTimeScale" | "setGold" | "teleportPlayer" | "trace" | "placeShip" | "setSupplies" | "spawnNpc" | "setShipTier" | "setLivery" | "royale" | "damage";

export interface DebugMsg {
  cmd: DebugCommand;
  args: (string | number)[];
}

/** "new" starts a crew in the lowest free slot; a number joins that crew slot. */
export type CrewChoice = "new" | number;

export interface JoinOptions {
  name: string;
  crew?: CrewChoice;
  /** Character look code (look.ts); omitted or invalid means the classic crewmate. */
  look?: string;
}

// ---------------------------------------------------------------------------
// Validation of untrusted payloads (server side). Each returns a typed value or null.

const bounded = (lo: number, hi: number) => z.number().finite().min(lo).max(hi);

const InputSchema = z.strictObject({
  seq: z.number().int().min(0).max(0xffffffff),
  mx: bounded(-1, 1),
  mz: bounded(-1, 1),
  yaw: bounded(-1000, 1000),
  pitch: bounded(-2, 2),
  yawFrame: z.number().int().min(0).max(SHIP_SLOTS),
  jump: z.boolean(),
  dive: z.boolean(),
  sprint: z.boolean(),
  crouch: z.boolean(),
  scope: z.boolean(),
});
const ShootSchema = InputSchema.pick({ yaw: true, pitch: true, yawFrame: true });

const Slot4 = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
const LeakTarget = z.templateLiteral(["leak:", Slot4]);
const IslandCannonTarget = z.templateLiteral(["islandCannon:", z.enum(ISLAND_IDS)]);
const indexTarget = <P extends string>(prefix: P, count: number) => z.string().regex(new RegExp(`^${prefix}:\\d{1,3}$`))
  .refine((s) => Number(s.slice(prefix.length + 1)) < count)
  .transform((s) => s as `${P}:${number}`);
const CannonTarget = indexTarget("cannon", SHIP_POINTS.cannons.length);
const ShipLanternTarget = indexTarget("shipLantern", SHIP_POINTS.lanterns.length);
const BarrelTarget = indexTarget("barrel", SUPPLY_BARRELS.length);
const LeverTarget = indexTarget("lever", GATE_LEVERS.length);
const TelescopeTarget = indexTarget("telescope", TELESCOPES.length);
const LanternTarget = z.templateLiteral(["lantern:", z.enum(LANTERN_INTERACTION_IDS)]);
const ChestTarget = z
  .string()
  .regex(/^chest:[A-Za-z0-9]{1,24}$/)
  .transform((s) => s as `chest:${string}`);
const UseSchema = z.strictObject({
  target: z.union([z.enum(["wheel", "capstan", "questBoard:map", "questBoard:hunt", "merchant", "bucket", "supplies", "bell", "nestScope"]), LeakTarget, CannonTarget, ShipLanternTarget, IslandCannonTarget, BarrelTarget, LeverTarget, TelescopeTarget, ChestTarget, LanternTarget]),
});
const RopeTarget = indexTarget("rope", ROPES.length);
const SailTarget = indexTarget("sail", SAILS.length);
const HoldSchema = z.strictObject({ target: z.union([z.enum(["capstan", "wheel"]), LeakTarget, CannonTarget, RopeTarget, SailTarget, z.null()]) });
const SailSchema = z.strictObject({ delta: z.union([z.literal(1), z.literal(-1)]) });
const VoteSchema = z.strictObject({ yes: z.boolean() });
const BotSchema = z.strictObject({ add: z.boolean() });
const RecoverSchema = z.strictObject({ cancel: z.boolean() });
const EmptySchema = z.union([z.undefined(), z.null(), z.strictObject({})]);
const EquipSchema = z.strictObject({ equipment: z.enum(["sword", "shovel", "spyglass", "pistol"]) });
const AttackSchema = z.strictObject({ requestId: z.number().int().min(0).max(0xffffffff) });
const BlockSchema = z.strictObject({ active: z.boolean() });
const CrewChoiceSchema = z.union([z.literal("new"), z.number().int().min(0).max(CREW.maxCrews - 1)]);
const CrewSchema = z.strictObject({ choice: CrewChoiceSchema });
const JoinCrewSchema = z.object({ crew: CrewChoiceSchema });
const DebugSchema = z.strictObject({
  cmd: z.enum(["teleportShip", "forceQuest", "setFlooding", "openLeak", "setTimeScale", "setGold", "teleportPlayer", "trace", "placeShip", "setSupplies", "spawnNpc", "setShipTier", "setLivery", "royale", "damage"]),
  args: z
    .array(z.union([z.string().max(32), z.number().finite()]))
    .max(4)
    .default([]),
});

export function parseInput(v: unknown): InputMsg | null {
  const r = InputSchema.safeParse(v);
  return r.success ? r.data : null;
}

export function parseShoot(v: unknown): ShootMsg | null {
  const r = ShootSchema.safeParse(v);
  return r.success ? r.data : null;
}

export function parseUseTarget(v: unknown): UseTarget | null {
  const r = UseSchema.safeParse(v);
  return r.success ? r.data.target : null;
}

/** `{ target: HoldTarget }` starts a hold; `{ target: null }` ends it. Returns undefined when invalid. */
export function parseHold(v: unknown): HoldTarget | null | undefined {
  const r = HoldSchema.safeParse(v);
  return r.success ? r.data.target : undefined;
}

export function parseSail(v: unknown): -1 | 1 | null {
  const r = SailSchema.safeParse(v);
  return r.success ? r.data.delta : null;
}

export function parseVote(v: unknown): boolean | null {
  const r = VoteSchema.safeParse(v);
  return r.success ? r.data.yes : null;
}

export function parseBot(v: unknown): boolean | null {
  const r = BotSchema.safeParse(v);
  return r.success ? r.data.add : null;
}

export function parseRecover(v: unknown): boolean | null {
  const r = RecoverSchema.safeParse(v);
  return r.success ? r.data.cancel : null;
}

/** Empty-payload messages (dig, drop, abandon) accept only an empty object or nothing. */
export function parseEmpty(v: unknown): boolean {
  return EmptySchema.safeParse(v).success;
}

export function parseEquip(v: unknown): Tool | null {
  const r = EquipSchema.safeParse(v);
  return r.success ? r.data.equipment : null;
}

export function parseAttack(v: unknown): AttackMsg | null {
  const r = AttackSchema.safeParse(v);
  return r.success ? r.data : null;
}

export function parseBlock(v: unknown): boolean | null {
  const r = BlockSchema.safeParse(v);
  return r.success ? r.data.active : null;
}

export function parseCrewChoice(v: unknown): CrewChoice | null {
  const r = CrewSchema.safeParse(v);
  return r.success ? r.data.choice : null;
}

/** Crew choice from join options; undefined when absent or invalid. */
export function parseJoinCrew(options: unknown): CrewChoice | undefined {
  const r = JoinCrewSchema.safeParse(options);
  return r.success ? r.data.crew : undefined;
}

export function parseDebug(v: unknown): DebugMsg | null {
  const r = DebugSchema.safeParse(v);
  return r.success ? r.data : null;
}

/** Trims, strips control characters, collapses whitespace; null when empty. */
export function sanitizeName(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const cleaned = raw
    .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, NAME_MAX)
    .trim();
  return cleaned.length > 0 ? cleaned : null;
}

/** Appends " (2)", " (3)" … until the name is unique among `taken`. */
export function uniqueName(name: string, taken: Iterable<string>): string {
  const set = new Set(taken);
  if (!set.has(name)) return name;
  for (let n = 2; ; n++) {
    const suffix = ` (${n})`;
    const candidate = name.slice(0, NAME_MAX - suffix.length).trimEnd() + suffix;
    if (!set.has(candidate)) return candidate;
  }
}

